"use client";

import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  ArrowRight,
  ArrowUpRight,
  Award,
  Box,
  Brush,
  CheckCircle2,
  Crosshair,
  BookOpenText,
  FileCode2,
  Image,
  ImageDown,
  Package,
  Palette,
  ScanSearch,
  Scissors,
  Shirt,
  Sparkles,
  UserPlus,
  Wand2,
  Workflow,
  Zap,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { ScrollReveal } from "./ScrollReveal";
import { LandingSectionHeader } from "./LandingSectionHeader";
import { useAuth } from "@/components/auth/AuthProvider";
import { useServiceSettings } from "@/hooks/useSiteSettings";
import type { ServiceKey, ServiceSettingsRow } from "@/hooks/useSiteSettings";
import { usePalette } from "@/components/theme/PaletteProvider";
import { pickPaletteCardImage } from "@/lib/toolPreviewImages";
import { useRecentScroll } from "@/hooks/useRecentScroll";
import type { FeatureKey } from "@/components/auth/AuthProvider";
import { hasFeature } from "@/lib/billing/planLabel";
import { assetCategoryToolRank } from "@/components/hub/hubData";
import { cn } from "@/lib/utils";

// ─── Static data ──────────────────────────────────────────────────────────────

type LandingToolKey = ServiceKey;
type LandingToolCard = ServiceSettingsRow;

const SERVICE_ICONS: Record<LandingToolKey, LucideIcon> = {
  paintboothstudio: Brush,
  paintbooth: Palette,
  liverymagic: Wand2,
  itemimage: Image,
  badgemaker: Award,
  embroiderytool: Scissors,
  uvstudio: Box,
  eupviewer: Shirt,
  addoneupcreator: UserPlus,
  weaponviewer: Crosshair,
  blinklab: Zap,
  metaroom: FileCode2,
  packbench: Package,
  codesmith: ScanSearch,
  codevisualizer: Workflow,
  scriptdocs: BookOpenText,
  tripoai: Sparkles,
  chevronbuilder: AlertTriangle,
  creatorconvert: ImageDown,
  inklab: Sparkles,
};

const SERVICE_FILE_TYPES: Record<LandingToolKey, string[]> = {
  paintboothstudio: [".psd", ".pdn", ".xcf", ".png"],
  paintbooth: [".psd", ".pdn", ".xcf", ".png"],
  liverymagic: [".png", ".webp", ".jpg"],
  itemimage: [".png", ".webp"],
  badgemaker: [".png", ".webp"],
  embroiderytool: [".png", ".webp", ".jpg"],
  uvstudio: [".yft", ".obj", ".png"],
  eupviewer: [".ydd", ".ytd"],
  addoneupcreator: [".ydd", ".ytd", ".lua", ".zip"],
  weaponviewer: [".ydr", ".ytd", ".png"],
  blinklab: [".xml", ".dat", ".json"],
  metaroom: [".meta", ".xml"],
  packbench: [".yft", ".ytd", ".lua", ".zip"],
  codesmith: [".lua", ".js", ".zip", ".pdf"],
  codevisualizer: [".lua", ".js", ".ts", ".json", ".cfg", ".xml", ".meta", ".zip"],
  scriptdocs: [".lua", ".js", ".ts", ".json", ".cfg", ".xml", ".meta", ".zip", ".md"],
  tripoai: [".glb", ".obj"],
  chevronbuilder: [".png"],
  creatorconvert: [".dds", ".ytd", ".zip", ".png", ".webp", ".jpg"],
  inklab: [".png", ".webp"],
};

const AI_SERVICE_KEYS  = new Set<LandingToolKey>(["tripoai", "itemimage", "badgemaker", "embroiderytool", "inklab"]);
const NEW_SERVICE_KEYS = new Set<LandingToolKey>(["codevisualizer", "scriptdocs", "chevronbuilder", "creatorconvert"]);

function LandingAiBadge() {
  return (
    <span className="inline-flex h-5 items-center rounded-full border border-[#8d532c] bg-[#2a1c12] px-2.5 font-mono text-[8px] uppercase leading-none tracking-[0.14em] text-[#d08a54]">
      AI
    </span>
  );
}

function LandingNewBadge() {
  return (
    <span className="inline-flex h-5 items-center rounded-full border border-emerald-400/40 bg-emerald-400/10 px-2.5 font-mono text-[8px] uppercase leading-none tracking-[0.14em] text-emerald-400">
      New
    </span>
  );
}

const DEFAULT_SERVICE_CARDS: Record<LandingToolKey, LandingToolCard> = {
  paintboothstudio: {
    key: "paintboothstudio",
    name: "Paint Booth Studio",
    tagline: "Layer-Based Livery Studio",
    description: "Edit liveries on a Photoshop-style layered canvas while seeing the result live on a 3D vehicle preview.",
    logo_url: null,
    card_image_url: "https://comms.space101.store/storage/v1/object/public/site-assets/services/paintbooth/card/1776900255488-ed023e9a22e788.png",
    gradient: "from-amber-500/20 to-orange-500/20",
    href: "/paintbooth-studio",
    sort_order: 0,
    updated_at: null,
  },
  paintbooth: {
    key: "paintbooth",
    name: "Paint Booth",
    tagline: "3D Livery Viewer",
    description: "Upload textures, tweak colors, adjust lighting, and preview your livery on 3D models in real time. Supports PSD, PDN, XCF file formats for live editing.",
    logo_url: null,
    card_image_url: "https://comms.space101.store/storage/v1/object/public/site-assets/services/paintbooth/card/1776900255488-ed023e9a22e788.png",
    gradient: "from-amber-500/20 to-orange-500/20",
    href: "/paintbooth",
    sort_order: 0,
    updated_at: null,
  },
  liverymagic: {
    key: "liverymagic",
    name: "Livery Magic",
    tagline: "AI Livery Generator",
    description: "Upload a vehicle template, add an optional reference image, and generate a FiveM-ready livery texture.",
    logo_url: null,
    card_image_url: null,
    gradient: "from-lime-500/20 to-sky-500/20",
    href: "/livery-magic",
    sort_order: 1,
    updated_at: null,
  },
  itemimage: {
    key: "itemimage",
    name: "Asset Art",
    tagline: "AI Inventory Icon Generator",
    description: "Type any item name and generate a transparent, game-ready inventory icon for your FiveM server.",
    logo_url: null,
    card_image_url: "https://fkcobnobusicmldplods.supabase.co/storage/v1/object/public/site-assets/services/itemimage/card/1777395949631-3bce045e440978.png",
    gradient: "from-violet-500/20 to-pink-500/20",
    href: "/item-image",
    sort_order: 2,
    updated_at: null,
  },
  // Ink Lab is admin-gated (not in LANDING_CATEGORIES), so this card never
  // renders on the marketing grid yet — it exists only to satisfy the total
  // Record<ServiceKey, …>. Add "inklab" to a LANDING_CATEGORIES group at launch.
  inklab: {
    key: "inklab",
    name: "Ink Lab",
    tagline: "AI Tattoo Builder",
    description: "Generate custom MP freemode tattoos with guided AI, then export a drop-in FiveM addon.",
    logo_url: null,
    card_image_url: null,
    gradient: "from-violet-500/20 to-rose-500/20",
    href: "/ink-lab",
    sort_order: 18,
    updated_at: null,
  },
  badgemaker: {
    key: "badgemaker",
    name: "Badge Maker",
    tagline: "AI Badge & Emblem Generator",
    description: "Generate crests, badges, and emblems for liveries, departments, and crews.",
    logo_url: null,
    card_image_url: null,
    gradient: "from-fuchsia-500/20 to-amber-500/20",
    href: "/badge-maker",
    sort_order: 3,
    updated_at: null,
  },
  embroiderytool: {
    key: "embroiderytool",
    name: "Embroidery Tool",
    tagline: "AI Embroidered Patch Converter",
    description: "Upload logos, badges, icons, or patch art and convert them into stitched embroidered PNG patches with real transparent backgrounds.",
    logo_url: null,
    card_image_url: null,
    gradient: "from-rose-500/20 to-sky-500/20",
    href: "/embroidery-tool",
    sort_order: 4,
    updated_at: null,
  },
  uvstudio: {
    key: "uvstudio",
    name: "UV Studio",
    tagline: "UV Template Export",
    description: "Render the UV map template for models that are already mapped but missing their original template.",
    logo_url: null,
    card_image_url: "https://comms.space101.store/storage/v1/object/public/site-assets/services/uvstudio/card/1776900338483-c2f6947e88043.png",
    gradient: "from-yellow-500/20 to-amber-500/20",
    href: "/uv-studio",
    sort_order: 5,
    updated_at: null,
  },
  eupviewer: {
    key: "eupviewer",
    name: "EUP Viewer",
    tagline: "YDD Outfit Preview",
    description: "Upload YDDs, tweak colors, and preview your textures on 3D models in real time.",
    logo_url: null,
    card_image_url: "https://comms.space101.store/storage/v1/object/public/site-assets/services/eupviewer/card/1776900349223-51be3d088c0008.png",
    gradient: "from-orange-500/20 to-red-500/20",
    href: "/eup-viewer",
    sort_order: 6,
    updated_at: null,
  },
  addoneupcreator: {
    key: "addoneupcreator",
    name: "Addon EUP Creator",
    tagline: "EUP Pack Scaffolder",
    description: "Convert EUP clothing files between single-player, non-addon FiveM, and addon FiveM folder structures.",
    logo_url: null,
    card_image_url: null,
    gradient: "from-cyan-500/20 to-indigo-500/20",
    href: "/addon-eup-creator",
    sort_order: 7,
    updated_at: null,
  },
  weaponviewer: {
    key: "weaponviewer",
    name: "Weapon Designer",
    tagline: "YDR Weapon Preview",
    description: "Upload weapons, preview your textures on the 3D model in real time. Supports PSD, PDN, XCF file formats for live editing.",
    logo_url: null,
    card_image_url: "https://comms.space101.store/storage/v1/object/public/site-assets/services/weaponviewer/card/1776881149662-05457de076a47.png",
    gradient: "from-rose-500/20 to-slate-500/20",
    href: "/weapon-viewer",
    sort_order: 8,
    updated_at: null,
  },
  blinklab: {
    key: "blinklab",
    name: "Blink Lab",
    tagline: "Siren Pattern Editor",
    description: "Design lightbar flash patterns on an 8-color grid, preview live, and export sequences as binary or decimal.",
    logo_url: null,
    card_image_url: "https://comms.space101.store/storage/v1/object/public/site-assets/services/blinklab/card/1776900559060-6009873edde638.png",
    gradient: "from-amber-500/20 to-red-500/20",
    href: "/blinklab",
    sort_order: 9,
    updated_at: null,
  },
  metaroom: {
    key: "metaroom",
    name: "Easy Meta",
    tagline: "Vehicle Meta Generator",
    description: "Generate vehicles.meta, handling.meta, carvariations.meta, and carcols.meta files with a guided editor and exports.",
    logo_url: null,
    card_image_url: "https://comms.space101.store/storage/v1/object/public/site-assets/services/metaroom/card/1776900615450-b5ac90813e7818.png",
    gradient: "from-stone-400/20 to-amber-500/20",
    href: "/easy-meta",
    sort_order: 10,
    updated_at: null,
  },
  packbench: {
    key: "packbench",
    name: "PackBench",
    tagline: "FiveM Resource Builder",
    description: "Drop .yft, .ytd, and meta files to generate a correctly structured FiveM resource ZIP with fxmanifest.lua.",
    logo_url: null,
    card_image_url: "https://comms.space101.store/storage/v1/object/public/site-assets/services/packbench/card/1776900418164-20b4a3241e75e8.png",
    gradient: "from-orange-500/20 to-stone-500/20",
    href: "/pack-bench",
    sort_order: 11,
    updated_at: null,
  },
  codesmith: {
    key: "codesmith",
    name: "Code Smith",
    tagline: "FiveM Resource Scanner",
    description: "Scan zipped resources for loop abuse, missing Wait() calls, heavy events, and potentially unused assets.",
    logo_url: null,
    card_image_url: "https://comms.space101.store/storage/v1/object/public/site-assets/services/codesmith/card/1776900547164-5fa69e2d11ee7.png",
    gradient: "from-amber-500/20 to-red-500/20",
    href: "/code-smith",
    sort_order: 12,
    updated_at: null,
  },
  codevisualizer: {
    key: "codevisualizer",
    name: "Code Visualizer",
    tagline: "FiveM Script Map",
    description: "Map FiveM scripts into readable command, event, export, function, and trigger flows with an embedded code editor.",
    logo_url: null,
    card_image_url: "https://comms.space101.store/storage/v1/object/public/site-assets/services/codesmith/card/1776900547164-5fa69e2d11ee7.png",
    gradient: "from-cyan-500/20 to-blue-500/20",
    href: "/code-visualizer",
    sort_order: 13,
    updated_at: null,
  },
  scriptdocs: {
    key: "scriptdocs",
    name: "Script Docs",
    tagline: "Resource Documentation Generator",
    description: "Generate README-ready docs from resource commands, exports, events, dependencies, and config files.",
    logo_url: null,
    card_image_url: null,
    gradient: "from-sky-500/20 to-emerald-500/20",
    href: "/script-docs",
    sort_order: 14,
    updated_at: null,
  },
  tripoai: {
    key: "tripoai",
    name: "Model Forge",
    tagline: "AI 3D Model Generator",
    description: "Generate 3D models from text prompts or reference images using Tripo AI. Output is a GLB file ready for Blender, UV Studio, or any FiveM-ready workflow.",
    logo_url: null,
    card_image_url: "https://fkcobnobusicmldplods.supabase.co/storage/v1/object/public/site-assets/services/tripoai/card/1777985508588-c3a6516dd52148.png",
    gradient: "from-violet-500/20 to-cyan-500/20",
    href: "/model-forge",
    sort_order: 15,
    updated_at: null,
  },
  chevronbuilder: {
    key: "chevronbuilder",
    name: "Chevron Builder",
    tagline: "Rear Warning Pattern Generator",
    description: "Generate professional rear warning chevrons for Fire, EMS, DOT, tow, and emergency vehicles with custom colors, angles, reflective preview, and UV-aware placement.",
    logo_url: null,
    card_image_url: null,
    gradient: "from-amber-500/20 to-red-600/20",
    href: "/chevron-builder",
    sort_order: 16,
    updated_at: null,
  },
  creatorconvert: {
    key: "creatorconvert",
    name: "Creator Convert",
    tagline: "Texture Conversion Utility",
    description: "Convert DDS textures, create DDS files from images, and extract selected YTD textures into a ZIP archive.",
    logo_url: null,
    card_image_url: null,
    gradient: "from-orange-500/20 to-stone-600/20",
    href: "/creator-convert",
    sort_order: 17,
    updated_at: null,
  },
};

// ─── Categories ───────────────────────────────────────────────────────────────

type LandingCategoryId = "vehicles" | "props" | "clothing" | "weapons" | "scripts";
type LandingFilter = LandingCategoryId | "all";
type LandingCategory = { id: LandingCategoryId; label: string; hint: string; keys: LandingToolKey[] };

// Tools grouped by the asset they produce — the same segmentation the hub uses
// (Vehicles / Clothing / Props / Weapons / Scripts). Order within a group is
// re-sorted by sort_order at render time, so the key order here is incidental.
const LANDING_CATEGORIES: LandingCategory[] = [
  { id: "vehicles", label: "Vehicles", hint: "Liveries, meta, lights, and textures.",     keys: ["paintboothstudio", "paintbooth", "uvstudio", "blinklab", "chevronbuilder", "creatorconvert", "metaroom"] },
  { id: "clothing", label: "Clothing", hint: "EUP outfits, patches, badges, and add-on packs.", keys: ["eupviewer", "addoneupcreator", "badgemaker", "embroiderytool"] },
  { id: "props",    label: "Props",    hint: "Generate 3D models and item art.",          keys: ["tripoai", "itemimage"] },
  { id: "weapons",  label: "Weapons",  hint: "Weapon textures and 3D previews.",          keys: ["weaponviewer"] },
  { id: "scripts",  label: "Scripts",  hint: "Audit, package, and document resources.",   keys: ["codesmith", "codevisualizer", "scriptdocs", "packbench"] },
];

// Single source of truth for "how many tools" across the landing page — derived
// from the categories above so the headline + stats strip stay in sync whenever
// a tool is added or removed (no more hand-editing "15" / "Fifteen").
export const LANDING_TOOL_COUNT = LANDING_CATEGORIES.reduce((n, c) => n + c.keys.length, 0);

// ─── Helpers ──────────────────────────────────────────────────────────────────

function isExternal(href: string) {
  return /^https?:\/\//i.test(href);
}

function getLandingFeatureKey(key: LandingToolKey): FeatureKey | undefined {
  if (key === "paintboothstudio") return "paintbooth";
  return key;
}

function getLandingToolHref(card: LandingToolCard, canOpenFullTool: boolean) {
  if (canOpenFullTool) return card.href?.trim() || null;
  if (card.key === "paintbooth") return "/paintbooth-demo";
  if (card.key === "paintboothstudio") return "/paintbooth-studio-demo";
  return card.href?.trim() || null;
}

// ─── Main component ───────────────────────────────────────────────────────────

export function LandingToolsGrid() {
  const { permissions, isAdmin } = useAuth();
  const { data: serviceSettings = [] } = useServiceSettings();

  const servicesByKey = useMemo(
    () => new Map(serviceSettings.map((s) => [s.key, s])),
    [serviceSettings],
  );
  const resolve = (key: LandingToolKey): LandingToolCard => {
    const fallback = DEFAULT_SERVICE_CARDS[key];
    const row = servicesByKey.get(key);
    // Paint Booth Studio keeps its curated landing copy, but syncs its preview
    // image from the DB so it always matches the hub / home page card.
    if (key === "paintboothstudio") {
      if (!row) return fallback;
      // Keep the curated landing copy but sync both palette card images from DB.
      return {
        ...fallback,
        card_image_url: row.card_image_url ?? fallback.card_image_url,
        card_image_url_cobalt: row.card_image_url_cobalt ?? fallback.card_image_url_cobalt ?? null,
      };
    }
    return row ? { ...fallback, ...row } : fallback;
  };

  const sortOrderOf = (key: LandingToolKey) => {
    const fallback = DEFAULT_SERVICE_CARDS[key];
    const row = servicesByKey.get(key);
    return (row ? { ...fallback, ...row } : fallback).sort_order;
  };

  // Categories with their keys sorted by the owner-curated order shared with the
  // home grid + hub (ASSET_CATEGORY_TOOL_ORDER), falling back to the
  // admin-controlled sort_order for any category/tool not curated there.
  const categories = useMemo(
    () =>
      LANDING_CATEGORIES.map((cat) => ({
        ...cat,
        keys: [...cat.keys].sort((a, b) => {
          const aCur = assetCategoryToolRank(cat.id, DEFAULT_SERVICE_CARDS[a].href ?? "");
          const bCur = assetCategoryToolRank(cat.id, DEFAULT_SERVICE_CARDS[b].href ?? "");
          if (aCur !== bCur) {
            if (aCur === -1) return 1;
            if (bCur === -1) return -1;
            return aCur - bCur;
          }
          return sortOrderOf(a) - sortOrderOf(b);
        }),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [servicesByKey],
  );
  const allKeys = useMemo(() => categories.flatMap((c) => c.keys), [categories]);

  const [activeCategory, setActiveCategory] = useState<LandingFilter>("all");
  const visibleCategories = useMemo(
    () => (activeCategory === "all" ? categories : categories.filter((c) => c.id === activeCategory)),
    [activeCategory, categories],
  );
  const visibleKeys = useMemo(() => visibleCategories.flatMap((c) => c.keys), [visibleCategories]);

  const [activeKey, setActiveKey] = useState<LandingToolKey>(allKeys[0]);
  const displayCard = resolve(activeKey);

  // Keep the detail panel pointed at a visible tool: when the active filter
  // changes (or a tool disappears), snap to the first visible card.
  useEffect(() => {
    if (!visibleKeys.includes(activeKey)) {
      setActiveKey(visibleKeys[0] ?? allKeys[0]);
    }
  }, [activeKey, visibleKeys, allKeys]);

  const tabs: { id: LandingFilter; label: string; count: number }[] = [
    { id: "all", label: "All", count: allKeys.length },
    ...categories.map((c) => ({ id: c.id, label: c.label, count: c.keys.length })),
  ];

  // Hover switches the active segment instantly — but only on genuine pointer
  // movement. Suppress it mid-scroll so the tab row sliding under a stationary
  // cursor doesn't fire mouseenter and collapse the grid to one category.
  const scrolledRecently = useRecentScroll();

  const renderToolCard = (key: LandingToolKey) => {
    const card = resolve(key);
    const Icon = SERVICE_ICONS[key];
    const active = activeKey === key;
    const canOpenFullTool = hasFeature(permissions, getLandingFeatureKey(key), { isAdmin });
    const href = getLandingToolHref(card, canOpenFullTool);

    const cardClass = cn(
      "group relative flex min-h-[8rem] flex-col gap-3 rounded-2xl border p-4 transition duration-200",
      active
        ? "scale-[1.02] border-[hsl(var(--lb-ink)/0.3)] bg-[hsl(var(--lb-panel))] shadow-sm shadow-[hsl(var(--lb-ink)/0.08)]"
        : "border-[hsl(var(--lb-line))] bg-[hsl(var(--lb-panel)/0.5)] hover:border-[hsl(var(--lb-line)/1.4)] hover:bg-[hsl(var(--lb-panel))]",
    );

    const cardContent = (
      <>
        {/* Explore label */}
        <span className="absolute right-3 top-3 flex items-center gap-1 -translate-x-2 text-[11px] font-medium text-[hsl(var(--lb-muted))] opacity-0 transition duration-300 group-hover:translate-x-0 group-hover:opacity-100">
          Explore Tool
          <ArrowRight className="h-3 w-3" />
        </span>

        {/* Icon */}
        <span
          className={cn(
            "grid h-9 w-9 flex-none place-items-center rounded-xl border transition duration-200",
            active
              ? "border-[hsl(var(--lb-ink)/0.3)] bg-[hsl(var(--lb-ink))] text-[hsl(var(--lb-bg))]"
              : "border-[hsl(var(--lb-line))] bg-[hsl(var(--lb-bg-soft))] text-[hsl(var(--lb-muted))] group-hover:border-[hsl(var(--lb-ink)/0.3)] group-hover:bg-[hsl(var(--lb-ink))] group-hover:text-[hsl(var(--lb-bg))]",
          )}
        >
          <Icon className="h-5 w-5" />
        </span>

        {/* Title + description */}
        <div className="flex-1">
          <p className={cn("text-base font-semibold tracking-tight", active ? "text-[hsl(var(--lb-ink))]" : "text-[hsl(var(--lb-ink)/0.85)]")}>
            <span className="flex items-center gap-1.5">
              <span>{card.name}</span>
              {AI_SERVICE_KEYS.has(key) ? <LandingAiBadge /> : NEW_SERVICE_KEYS.has(key) ? <LandingNewBadge /> : null}
            </span>
          </p>
          <p className="mt-1.5 line-clamp-3 text-[12.5px] leading-relaxed text-[hsl(var(--lb-muted))]">
            {card.description}
          </p>
          <div className="mt-2 flex flex-wrap gap-1">
            {SERVICE_FILE_TYPES[key].map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center rounded border border-[hsl(var(--success)/0.4)] bg-[hsl(var(--success)/0.1)] px-1.5 py-px font-mono text-[9px] tracking-wide text-success"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      </>
    );

    if (!href) {
      return (
        <div key={key} className={cardClass} onMouseEnter={() => setActiveKey(key)}>
          {cardContent}
        </div>
      );
    }
    if (isExternal(href)) {
      return (
        <a key={key} href={href} target="_blank" rel="noreferrer" className={cardClass} onMouseEnter={() => setActiveKey(key)}>
          {cardContent}
        </a>
      );
    }
    return (
      <Link
        key={key}
        to={href}
        className={cardClass}
        onMouseEnter={() => setActiveKey(key)}
      >
        {cardContent}
      </Link>
    );
  };

  const comingSoonCard = (
    <div className="flex min-h-[8rem] flex-col gap-3 rounded-2xl border border-dashed border-[hsl(var(--lb-line)/0.6)] p-4 opacity-50">
      <span className="grid h-9 w-9 flex-none place-items-center rounded-xl border border-[hsl(var(--lb-line)/0.5)] bg-[hsl(var(--lb-bg-soft)/0.4)]">
        <Sparkles className="h-4 w-4 text-[hsl(var(--lb-muted)/0.5)]" />
      </span>
      <div className="flex-1">
        <p className="text-base font-semibold tracking-tight text-[hsl(var(--lb-muted))]">Coming Soon</p>
        <p className="mt-1 text-[12.5px] leading-relaxed text-[hsl(var(--lb-subtle)/0.7)]">More tools are in development.</p>
      </div>
    </div>
  );

  return (
    <section className="py-8 sm:py-14">
      <div id="tools" className="scroll-mt-20" />
      <div className="lb-container">
        <ScrollReveal>
          <LandingSectionHeader
            eyebrow="Tools"
            title={`${LANDING_TOOL_COUNT} tools, one production line.`}
            copy="Preview, generate, edit, scan, package, render — every FiveM asset workflow runs in the browser."
            align="center"
          />
        </ScrollReveal>

        {/* Category segmented control — hover (or focus/tap) to switch */}
        <ScrollReveal>
          <div className="mt-8 flex justify-center">
            <div
              role="tablist"
              aria-label="Filter tools by category"
              className="inline-flex max-w-full flex-wrap items-center justify-center gap-1 rounded-2xl border border-[hsl(var(--lb-line))] bg-[hsl(var(--lb-panel)/0.5)] p-1"
            >
              {tabs.map((tab) => {
                const isActive = activeCategory === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    onMouseEnter={() => {
                      if (!scrolledRecently()) setActiveCategory(tab.id);
                    }}
                    onFocus={() => setActiveCategory(tab.id)}
                    onClick={() => setActiveCategory(tab.id)}
                    className={cn(
                      "inline-flex items-center gap-1.5 rounded-xl px-3.5 py-1.5 text-[13px] font-medium transition duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--lb-ink)/0.35)]",
                      isActive
                        ? "bg-[hsl(var(--lb-ink))] text-[hsl(var(--lb-bg))] shadow-sm shadow-[hsl(var(--lb-ink)/0.18)]"
                        : "text-[hsl(var(--lb-muted))] hover:text-[hsl(var(--lb-ink))]",
                    )}
                  >
                    {tab.label}
                    <span
                      className={cn(
                        "font-mono text-[10px] leading-none tabular-nums",
                        isActive ? "text-[hsl(var(--lb-bg)/0.65)]" : "text-[hsl(var(--lb-subtle))]",
                      )}
                    >
                      {tab.count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </ScrollReveal>

        {/* Split layout: grid + detail panel */}
        <div className="mt-8 grid grid-cols-1 items-start gap-4 lg:grid-cols-[1fr_520px]">
          {/* Tool grid, grouped by category */}
          <div className="space-y-8">
            {visibleCategories.map((cat, catIdx) => {
              const isLast = catIdx === visibleCategories.length - 1;
              return (
                <div key={cat.id} className="space-y-3">
                  {activeCategory === "all" && (
                    <div className="flex items-baseline justify-between gap-3 border-b border-[hsl(var(--lb-line)/0.6)] pb-2">
                      <span className="flex items-baseline gap-2">
                        <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-[hsl(var(--lb-ink)/0.85)]">
                          {cat.label}
                        </span>
                        <span className="font-mono text-[10px] tabular-nums text-[hsl(var(--lb-subtle))]">
                          {cat.keys.length}
                        </span>
                      </span>
                      <span className="truncate text-[11px] text-[hsl(var(--lb-muted)/0.8)]">{cat.hint}</span>
                    </div>
                  )}
                  <div className="grid auto-rows-fr grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                    {cat.keys.map(renderToolCard)}
                    {activeCategory === "all" && isLast && comingSoonCard}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Detail panel — desktop only. Sticky offset clears the 57px sticky
              header (matches the scroll-mt-20 convention) so the header never
              paints over the panel's top when scrolled. */}
          <div className="hidden lg:block lg:sticky lg:top-20">
            <DetailPanel
              card={displayCard}
              fileTypes={SERVICE_FILE_TYPES[displayCard.key]}
              ctaHref={getLandingToolHref(
                displayCard,
                hasFeature(permissions, getLandingFeatureKey(displayCard.key), { isAdmin }),
              )}
              ctaLabel={
                (displayCard.key === "paintbooth" || displayCard.key === "paintboothstudio") &&
                !hasFeature(permissions, getLandingFeatureKey(displayCard.key), { isAdmin })
                  ? "Open Demo"
                  : `Open ${displayCard.name}`
              }
            />
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Detail panel ─────────────────────────────────────────────────────────────

function DetailPanel({
  card,
  fileTypes,
  ctaHref,
  ctaLabel,
}: {
  card: LandingToolCard;
  fileTypes: string[];
  ctaHref: string | null;
  ctaLabel: string;
}) {
  const Icon = SERVICE_ICONS[card.key];
  const { palette } = usePalette();
  const imageUrl = pickPaletteCardImage(card, palette);

  return (
    <div className="overflow-hidden rounded-2xl border border-[hsl(var(--lb-line))] bg-[hsl(var(--lb-panel))]">
      <div key={card.key} className="animate-in fade-in slide-in-from-bottom-2 duration-200">
      {/* Preview */}
      <div className="relative aspect-video overflow-hidden bg-[hsl(var(--lb-panel-strong))]">
        {imageUrl ? (
          <>
            <img
              src={imageUrl}
              alt=""
              aria-hidden
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-[hsl(var(--lb-panel))] to-transparent" />
          </>
        ) : (
          <div className="flex h-full items-center justify-center">
            <Icon className="h-28 w-28 text-[hsl(var(--lb-line))]" />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="space-y-4 border-t border-[hsl(var(--lb-line))] p-5">
        {/* Title */}
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-[1.6rem] font-semibold tracking-[-0.04em] text-[hsl(var(--lb-ink))]">
              {card.name}
            </h3>
            {AI_SERVICE_KEYS.has(card.key) ? <LandingAiBadge /> : NEW_SERVICE_KEYS.has(card.key) ? <LandingNewBadge /> : null}
          </div>
        </div>

        {/* Description */}
        <p className="text-sm leading-relaxed text-[hsl(var(--lb-muted))]">{card.description}</p>

        {/* File type badges */}
        <div className="flex flex-wrap gap-1.5">
          {fileTypes.map((ft) => (
            <span
              key={ft}
              className="rounded-full border border-[hsl(var(--lb-line))] px-2.5 py-0.5 font-mono text-[0.7rem] uppercase tracking-[0.14em] text-[hsl(var(--lb-subtle))]"
            >
              {ft}
            </span>
          ))}
        </div>

        {/* Bullets */}
        <ul className="space-y-1.5">
          {[card.tagline, ...fileTypes.slice(0, 2).map((ft) => `Exports ${ft} files`)].slice(0, 3).map((bullet) => (
            <li key={bullet} className="flex items-start gap-2 text-xs text-[hsl(var(--lb-muted))]">
              <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 flex-none text-[hsl(var(--lb-subtle))]" />
              {bullet}
            </li>
          ))}
        </ul>

        {/* CTA */}
        {ctaHref && (
          <div className="pt-1">
            {isExternal(ctaHref) ? (
              <a
                href={ctaHref}
                target="_blank"
                rel="noreferrer"
                className="flex items-center justify-center gap-1.5 rounded-full bg-[hsl(var(--lb-ink))] px-4 py-2.5 text-sm font-semibold text-[hsl(var(--lb-bg))] transition hover:opacity-85"
              >
                {ctaLabel}
                <ArrowUpRight className="h-3.5 w-3.5" />
              </a>
            ) : (
              <Link
                to={ctaHref}
                className="flex items-center justify-center gap-1.5 rounded-full bg-[hsl(var(--lb-ink))] px-4 py-2.5 text-sm font-semibold text-[hsl(var(--lb-bg))] transition hover:opacity-85"
              >
                {ctaLabel}
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            )}
          </div>
        )}
      </div>
      </div>
    </div>
  );
}
